
import { Injectable } from '@angular/core';
import { AppMetadataHandler } from '../../configs/app-metadata.handler'
import { CommonUtilty } from './commonUtility';
import { dateUtil } from './dateUtil';
import { ClaimPaymentDO } from 'app/BaseFramework/shared/models';
import { PolicyService } from 'app/FunctionalModules';
import { Constants } from 'app/constants';
import { InteractService } from '../services';
@Injectable()
export class claimClientUtil {
  constructor(
    public appMetadataHandler: AppMetadataHandler,
    public commonUtility: CommonUtilty,
    public dateUtil: dateUtil,
    public policyService: PolicyService,
    public interactService: InteractService
  ) {
  }

  ROLES = [
    'CLAIMANT',
    'PRIMARYINSURED',
    'INSURED',
    'OWNER',
    'NOMINEE',
    'APPOINTEE',
    'ASSIGNEE',
    'PAYER'
  ];
  constants = Constants;
  /**
   * Creates a nested role-policy-client map from input object (IO).
   * Also populates availableClientList for policyDO.
   * Example structure:
   * CLAIMANT → POL123 → GUID1 → { clientRefGuid: 'GUID1', name: 'John' }
   *
   * @param {Object} IO - Input data object containing client and policy details.
   * @returns {Object} clientMap - Nested map structured by role, policy, and clientRefGuid.
   */
  availableClientList = []
  /**
   * Enriches client list with additional fields like name, age, gender, email, etc.
   * Derived from clientDO and formatted accordingly.
   *
   * @param {Array} clientList - List of client objects.
   * @returns {Array} - Updated/enriched client list.
   */
  clientInformationMapping(clientList) {
    if (clientList && this.commonUtility.isArrayIterable(clientList)) {
      clientList.forEach((client) => {
        client["name"] = this.commonUtility.getFullNameFromClient(client.clientDO) || null;
        client["genderCd"] = this.commonUtility.getGenderFromClient(client.clientDO) || null;
        client["clientNum"] = client.clientDO["clientNum"] || null;
        client["dateOfBirth"] = this.dateUtil.getDateString( this.commonUtility.getDateOfBirthFromClient(client.clientDO)) || null;
        client["age"] = client["dateOfBirth"] ? this.calculateAge(client["dateOfBirth"]) : null;
        client["emailId"] = this.commonUtility.getEmailFromClient(client.clientDO) || null;
        client["id"] = client.clientDO["id"] || null;
        client["externalClientNum"] = client.clientDO["externalClientNum"] || null;
        client["courseCd"] = this.commonUtility.getSinglePersonEducationDOFromClient(client.clientDO);
        client["contactNum"] = this.commonUtility.getContactNumFromClient(client.clientDO) || null;
        client["policyNum"] = client["policyNum"]
          ? client["policyNum"]
          : client.clientDO["policyNum"]
          ? client.clientDO["policyNum"]
          : null;
        client["preferredIdentity"] = client.clientDO["preferredIdentity"] || null;
        client["nationality"] = this.commonUtility.getCitizenshipFromClient(client.clientDO) || null;
        client["roleCd"] =(client.roleCd == Constants.ROLES.BENEFICIARY ? Constants.ROLES.NOMINEE : client.roleCd) || null;
        client["relationship"] = client.clientDO.relationship || null;
        client["nomineeForInsured"] = client.clientDO.nomineeForInsured || null;
        client["isPayee"] = client.clientDO.isPayee || null;
        client["isPrimaryInsured"] = client.clientDO.isPrimaryInsured || null;
        client["isPayer"] = client.clientDO.isPayer || null;
        client["splitPercentage"] = client.clientDO.splitPercentage || null;
        client["selected"] = client.clientDO.selected || null;
        client["clientTypeCd"] = client.clientDO.clientTypeCd || null
      });
    }
    return clientList;
  }

  /**
   * Calculates client's age based on date of birth.
   *
   * @param {String} dob - Date of birth (string format).
   * @returns {Number|null} - Age in years or null if dob is invalid.
   */
  calculateAge(dob) {
    return dob
      ? new Date().getFullYear() -
          new Date(dob).getFullYear() -
          (new Date() <
          new Date(new Date(dob).setFullYear(new Date().getFullYear()))
            ? 1
            : 0)
      : null;
  }
  
  /**
   * Processes policies and maps unique clientDOs based on their GUIDs.
   * @param {Array} policiy - policy containing clientDOs.
   * @param {Map} clientDOMap - Map to store unique clientDOs with GUID as key.
   * @returns {Map} Updated Map with unique clientDOs.
   */
  processUniqueClients(policy, clientDOMap) {
    // Iterate clientDOs
    if (policy.clientDOs && Array.isArray(policy.clientDOs)) {
      policy.clientDOs.forEach((clientDO) => {
          clientDOMap.set(clientDO.guid, clientDO);
      });
    }
    return clientDOMap;
  }

  /**
   * Builds nested Map structure:
   * roleCd => policyNum => clientRefGuid => clientDO
   *
   * @param {Array} policies - List of policy objects
   * @param {Map} clientDOMap - Map of clientDOs (guid => clientDO)
   * @returns {Map} Nested Map: roleCd -> policyNum -> clientRefGuid -> clientDO
   */
  buildRolePolicyClientMap(roleMap,policy, clientDOMap) {
    const policyDO = policy.policyDO;
    if (!policyDO) return;

    const policyNum = policyDO.policyNum;
    const policyClientList = policyDO.policyClientList;

    // Filter & normalize client list
    const filteredClients = this.filterAndNormalizeClients(policyClientList);

    filteredClients.forEach((clientEntry) => {
      const roleCd = clientEntry.roleCd;
      const clientRefGuid = clientEntry.clientRefGuid;

      // Fetch clientDO from clientDOMap using guid
      const clientDO = clientDOMap.get(clientRefGuid);
      if (!clientDO) return; // skip if clientDO not found

      // Check if roleCd exists
      if (!roleMap.has(roleCd)) {
        roleMap.set(roleCd, new Map());
      }

      const policyMap = roleMap.get(roleCd);

      // Check if policyNum exists under roleCd
      if (!policyMap.has(policyNum)) {
        policyMap.set(policyNum, new Map());
      }

      const clientMap = policyMap.get(policyNum);

      // Add clientRefGuid => clientDO
      clientMap.set(clientRefGuid, clientDO);
    });

    return roleMap;
  }
  /**
   * Filters and normalizes policyClientList entries based on allowed roleCds.
   * Converts BENEFICIARY to NOMINEE.
   *
   * @param {Array} policyClientList - List of policy client objects
   * @returns {Array} Filtered & normalized client entries
   */
  filterAndNormalizeClients(policyClientList) {
    const allowedRoles = new Set([Constants.ROLES.INSURED,Constants.ROLES.BENEFICIARY,Constants.ROLES.OWNER,Constants.ROLES.ASSIGNEE,Constants.ROLES.APPOINTEE,Constants.ROLES.PAYEE,Constants.ROLES.DEPENDENT]);
    return policyClientList.filter((client) => allowedRoles.has(client.roleCd)).map((client) => {
        const normalizedRoleCd = client.roleCd === Constants.ROLES.BENEFICIARY ? Constants.ROLES.NOMINEE : client.roleCd;
        return { ...client, roleCd: normalizedRoleCd };
      });
  }

  /**
  * Flattens either rolePolicyClientMap or claimantClientMap to an array of client objects.
  * Handles both types of maps based on roleCd.
  * 
  * @param {Map} rolePolicyClientMap - Map structure differs based on role:
  *        - For CLAIMANT: Map<clientGuid, {clientDO, role}>
  *        - For other roles: Map<policyNum, Map<clientRefGuid, clientDO>>
  * @param {string} roleCd - RoleCd like 'INSURED', 'NOMINEE', 'CLAIMANT', etc.
  * @returns {Array} - Flattened array of clients with extra details
  */
  newFlattenClientDetails(rolePolicyClientMap, roleCd) {
    let flattenedClients = [];
    if (!rolePolicyClientMap) return flattenedClients;
    if (this.isClaimantLikeRole(roleCd)) {
      rolePolicyClientMap.forEach((value, clientGuid) => {
        flattenedClients.push({
          clientRefGuid: clientGuid,
          clientDO: value,
          roleCd: roleCd,
        });
      });
    } else {
      // For other roles, Map<policyNum, Map<clientGuid, clientDO>>
      rolePolicyClientMap.forEach((clientMap, policyNum) => {
        clientMap.forEach((clientDO, clientGuid) => {
          flattenedClients.push({
            policyNum: policyNum,
            clientRefGuid: clientGuid,
            clientDO: clientDO,
            roleCd: roleCd
          });
        });
      });
    }
    return flattenedClients;
  }
  
/**
 * Adds a clientDO object specifically for CLAIMANT role.
 * Since CLAIMANT clients do not have associated policyNum, 
 * they are stored in a flat claimantClientMap.
 * Also updates the global newClientDOMap to maintain all clients.
 * 
 * @param {Object} clientDO - The client Data Object to be added.
 */
addClaimantClient(clientDO, claimantClientMap, newClientDOMap) {
    if (!clientDO || !clientDO.guid) return;
  
    // Update global flat client map
    if (!newClientDOMap.has(clientDO.guid)) {
      newClientDOMap.set(clientDO.guid, clientDO);
    }
  
    // Set claimantClientMap using already stored clientDO
    if (!claimantClientMap.has(clientDO.guid)) {
      claimantClientMap.set(clientDO.guid, { 
        clientDO: newClientDOMap.get(clientDO.guid), 
        role: "CLAIMANT" 
      });
    }
  }
  
/**
 * Adds a clientDO object to the nested role-based and policy-based map structure.
 * Ensures clients are stored uniquely under their roleCd and policyNum.
 * Also updates the global newClientDOMap to maintain a flat list of all clients.
 * 
 * @param {Object} clientDO - The client Data Object to be added.
 * @param {String} roleCd - Role code (e.g., INSURED, OWNER, BENEFICIARY, etc.).
 * @param {String} policyNum - Policy number associated with the client.
 */
keyNum = 0;
addClientForPolicyRole1(clientDO, roleCd, policyNum, newRolePolicyClientMap, newClientDOMap) {
  if (!clientDO || !clientDO.guid || !roleCd || !policyNum) return;
 
  let computedPolicyNum =  typeof policyNum === 'string' ? policyNum :( Array.isArray(policyNum) && policyNum.length === 1 ? policyNum[0] : null);  
 
  // Initialize roleCd → policyNum → clientGuid → clientDO structure if missing
  if (!newRolePolicyClientMap.has(roleCd)) {
      newRolePolicyClientMap.set(roleCd, new Map());
  }
 
  let policyMap = newRolePolicyClientMap.get(roleCd);
 
  // Initialize policyNum → clientGuid → clientDO structure if missing
  if (!policyMap.has(computedPolicyNum)) {
      policyMap.set(computedPolicyNum, new Map());
  }
 
  let clientMap = policyMap.get(computedPolicyNum);
 
  // Add clientDO only if guid not already present to avoid duplication
  if (!clientMap.has(clientDO.guid)) {
      clientMap.set(clientDO.guid, clientDO);
  }
 
  // Update the flat map for quick lookup of all clients
  if (!newClientDOMap.has(clientDO.guid)) {
      newClientDOMap.set(clientDO.guid, clientDO);
  }
}

addClientForPolicyRole(clientDO, roleCd, claimantClientMap, newClientDOMap) {
  if (!clientDO || !clientDO.guid || !roleCd) return;

  // Initialize roleCd → clientGuid → clientDO structure if missing
  if (!claimantClientMap.has(roleCd)) {
    claimantClientMap.set(roleCd, new Map());
  }

  let clientMap = claimantClientMap.get(roleCd);

  // Add clientDO only if guid not already present to avoid duplication
  if (!clientMap.has(clientDO.guid)) {
    clientMap.set(clientDO.guid, clientDO);
  }

  // Update the flat map for quick lookup of all clients
  if (!newClientDOMap.has(clientDO.guid)) {
    newClientDOMap.set(clientDO.guid, clientDO);
  }
}


  /**
 * Updates the clientDO object in all related client maps (newClientDOMap, claimantClientMap, newRolePolicyClientMap).
 * 
 * @param {string} guid - Unique client identifier.
 * @param {Object} updatedClientDO - Updated client data object.
 * @param {Map} newClientDOMap - Global flat map holding clientDOs keyed by GUID.
 * @param {Map} claimantClientMap - Map holding claimant clients keyed by GUID.
 * @param {Map} newRolePolicyClientMap - Map holding role-based policy clients.
 */
  syncClientRecord(guid, updatedClientDO, newClientDOMap, claimantClientMap, newRolePolicyClientMap) {
    if (!guid || !updatedClientDO) return;  
    // 1. Update in newClientDOMap
    if (newClientDOMap.has(guid)) 
      newClientDOMap.set(guid, updatedClientDO);
  
    // 2. Update in claimantClientMap
    if (claimantClientMap.has(guid)) {
      let claimantObj = claimantClientMap.get(guid);
      if (claimantObj) {
        claimantObj.clientDO = updatedClientDO;
        claimantClientMap.set(guid, claimantObj); // Re-set updated object
      }
    }
  
    // Update in newRolePolicyClientMap
    newRolePolicyClientMap.forEach((policyMap, roleCd) => {
        // policyMap.forEach((clientMap, policyNum) => {
            if (policyMap.has(guid)) {
              policyMap.set(guid, updatedClientDO);
            }
        // });
     });
  
  }

  /**
 * Removes a specific policy from all roles and collects clientGuids from the removed policy.
 *
 * @param {Object} newRolePolicyClientMap - Map of roles, policies, and client mappings.
 * @param {String} policyToRemove - Policy number to remove.
 * @returns {Set} Set of deleted clientGuids.
 */
 removePolicyAndCollectClients(newRolePolicyClientMap, policyToRemove) {
    const deletedClientRefGuids = new Set();
      for (const [roleId, policyMap] of newRolePolicyClientMap) {
      if (policyMap.has(policyToRemove)) {
        const clientMap = policyMap.get(policyToRemove);
        for (const clientGuid of clientMap.keys()) {
          deletedClientRefGuids.add(clientGuid);
        }
        policyMap.delete(policyToRemove);
      }
    }
    // this.policyService.removePolicyFromCache(policyToRemove);

    return deletedClientRefGuids;
  }
  
    /**
 * Collects all clientGuids currently present in the newRolePolicyClientMap.
 *
 * @param {Object} newRolePolicyClientMap - Map of roles, policies, and client mappings.
 * @returns {Set} Set of all existing clientGuids.
 */
  collectRemainingClientGuids(newRolePolicyClientMap) {
    const existingClientGuids = new Set();
    for (const clientMap of newRolePolicyClientMap.values()) {
      for (const clientGuid of clientMap.keys()) {
        existingClientGuids.add(clientGuid);
      }
    }
    return existingClientGuids;
  }

/**
 * Removes client entries from the provided client map (`newClientDOMap`)
 * if they are no longer referenced in the `existingClientGuids` set.
 *
 * @param newClientDOMap - Map containing client data (keyed by client GUID)
 * @param deletedClientRefGuids - Set of client GUIDs potentially to be removed
 * @param existingClientGuids - Set of currently referenced client GUIDs
 */
  cleanUnreferencedClients(newClientDOMap, deletedClientRefGuids, existingClientGuids) {
    deletedClientRefGuids.forEach(clientGuid => {
      if (existingClientGuids.size === 0 || !existingClientGuids.has(clientGuid)) {
        newClientDOMap.delete(clientGuid);
      }
    });
  }

  /**
 * Removes a specific client reference GUID from the newRolePolicyClientMap
 * under the specified role and policy number.
 *
 * @param {string} clientRefGuid - The client reference GUID to be removed.
 * @param {string} policyNum - The policy number.
 * @param {string} roleId - The role ID.
 * @param {Map} rolePolicyClientMap - Map of role → policy → client → client details.
 * @returns {boolean} - Returns true if removal is successful, else false.
 */
  removeSpecificClient(clientRefGuid, capturedRole, newRolePolicyClientMap) {
    if (newRolePolicyClientMap && newRolePolicyClientMap.has(capturedRole)) {
      const roleMap = newRolePolicyClientMap.get(capturedRole);
      const clientDO = roleMap?.get(clientRefGuid);
      if (clientDO) {
        if (capturedRole === 'PRIMARYINSURED' && clientDO.isPrimaryInsured)
          clientDO.isPrimaryInsured = false;
        if (capturedRole === 'PAYER' && clientDO.isPayer)
          clientDO.isPayer = false;
        roleMap.delete(clientRefGuid);
        // this.interactService.sendUpdatedData(capturedRole);
        return true;
      }
    }
    return false;
  }

 /**
 * Processes server IO data to build maps for roles, policies, clients, and claimants
 * @param {Object} serverIO - The server input/output data
 * @returns {Object} An object containing three maps: rolePolicyClientMap, claimantMap, and clientMap
 */
 buildClientRolePolicyMappings(serverIO, clientDOHM, claimClientRoles_HM, claimantHM, claimPaymentsHM) {
  clientDOHM = this.processUniqueClients(serverIO, clientDOHM);
  const DOName = this.commonUtility.getDOName(serverIO);
  const clientList = DOName == 'caseDO' ? (serverIO[DOName].caseClients || []) : (serverIO[DOName].proposalClients || []);
  const claimPaymentsArray = serverIO[DOName].claimPayments || [];

  // Populate claimPaymentsHM
  claimPaymentsArray.forEach(payment => {
    if (payment.clientRefGuid) {
      claimPaymentsHM.set(payment.clientRefGuid, payment);
    }
  });

  clientList.forEach(clientRole => {
    let { clientRefGuid, policyNum, roleCd } = clientRole;
    let clientObj = clientDOHM.get(clientRefGuid);
    if (!clientObj) return;

    if (this.isClaimantLikeRole(roleCd)) {
      claimantHM.set(clientRefGuid, {
        clientDO: clientObj,
        role: Constants.ROLES.CLAIMANT
      });
    }

    if (!claimClientRoles_HM.has(roleCd)) {
      claimClientRoles_HM.set(roleCd, new Map());
    }

    let roleMap = claimClientRoles_HM.get(roleCd);

    if (roleCd === Constants.ROLES.PAYER) {
      clientObj.splitPercentage = clientRole.splitPercentage;
      clientObj.relationship = clientRole.relationCd;
      clientObj.isPayer = true;
    }
    if (roleCd === Constants.ROLES.INSURED) {
      clientObj.relationship = clientRole.relationCd;
    }

    if (roleCd === Constants.ROLES.PRIMARYINSURED) {
      clientObj.isPrimaryInsured = true;
      clientObj.relationship = clientRole.relationCd;
    }

    if (roleCd === Constants.ROLES.NOMINEE) {
      clientObj.relationship = clientRole.relationCd;
      clientObj.nomineeForInsured = clientRole.nomineeForInsured;
      clientObj.splitPercentage = clientRole.splitPercentage
    }

    roleMap.set(clientRefGuid, clientObj);
  });

  return { claimClientRoles_HM, claimantHM, clientDOHM, claimPaymentsHM };
}

/**
 * Removes entries associated with a null policy number from the role-based client map.
 * This helps maintain a clean structure by removing empty/null mappings after client assignment.
 *
 * @param nullMap - Map of role to policy-client mappings
 * @param clientData - Client data object containing role and clientDO details
 */
removeNullKeysFromMap(nullMap,clientData){
let policyMap = nullMap.get(clientData.roleCd);
  
  if (policyMap) {
    let nullMap = policyMap.get(null);

    if (nullMap && nullMap.has(clientData.clientDO.guid)) {
      nullMap.delete(clientData.clientDO.guid);

      if (nullMap.size === 0) {
        policyMap.delete(null); 
      }
    }
  }
}
/**
 * Initializes a new `ClaimPaymentDO` instance for a client-policy combination 
 * and adds it to the provided payment map.
 *
 * @param clientRefGuid - Unique identifier of the client
 * @param policyNum - Associated policy number
 * @param paymentDO - Map storing payment details keyed by client reference
 */
initializePaymentDO(clientRefGuid,policyNum,paymentDO) {
    let paymentDo = new ClaimPaymentDO();
    paymentDo["clientRefGuid"] = clientRefGuid;
    // paymentDo['policyNum'] = policyNum;
    paymentDO.set(clientRefGuid,paymentDo);
}
/**
 * Sets the `isPayee` flag for a nominee client under a specific policy.
 *
 * @param clientRefGuid - Client identifier
 * @param policyNum - Policy number
 * @param roleClientMap - Map containing role-to-policy-to-client mappings
 * @param event - Boolean flag indicating payee status
 */
setIsPayeeFlagForNominee(clientRefGuid,policyNum,roleClientMap,event){
  let nomineeMap = roleClientMap.get(Constants.ROLES.NOMINEE);
  let policyMap = nomineeMap.get(policyNum);
  let clientDO = policyMap.get(clientRefGuid);
  if(clientDO)
    clientDO.isPayee = event;
}

setisPrimaryInsured(clientRefGuid,policyNum,roleClientMap,event){
  let nomineeMap = roleClientMap.get(Constants.ROLES.PRIMARYINSURED);
  // let policyMap = nomineeMap.get(policyNum);
  let clientDO = nomineeMap.get(clientRefGuid);
  if(clientDO)
    clientDO.isPrimaryInsured = event;
}
setPayer(clientRefGuid,policyNum,roleClientMap,event){
  let nomineeMap = roleClientMap.get(Constants.ROLES.PAYER);
  // let policyMap = nomineeMap.get(policyNum);
  let clientDO = nomineeMap.get(clientRefGuid);
  if(clientDO)
    clientDO.isPayer = event;
}
/**
 * Calculates and sets the nominee split percentage for a client.
 * If payee flag is unchecked (event is false), resets the split first and recalculates it.
 *
 * @param clientRefGuid - Client identifier
 * @param rolePolicyClientMap - Role-to-policy-to-client map
 * @param policyNum - Policy number
 * @param event - Boolean indicating if nominee is marked as payee
 * @param paymentDO - Map of payment details
 */
calculateAndSetSplitPer(clientRefGuid, rolePolicyClientMap, policyNum, event, paymentDO) {
  const policyMap = rolePolicyClientMap.get(Constants.ROLES.NOMINEE)?.get(policyNum);
  if (!policyMap) return; 
  const matchingPayment = paymentDO.get(this.findPaymentKey(clientRefGuid,policyNum,paymentDO));
  if (!event) {
      this.resetSplitForClient(policyMap, clientRefGuid, matchingPayment);
      this.calculateAndSetSplitPer(clientRefGuid, rolePolicyClientMap, policyNum, true, paymentDO);
  } else {
      this.assignSplitToPayees(policyMap, matchingPayment);
  }
}

/**
 * Resets the split percentage for a client and updates the related payment object.
 *
 * @param policyMap - Client mapping under a policy
 * @param clientRefGuid - Client identifier
 * @param matchingPayment - Corresponding ClaimPaymentDO object
 */
resetSplitForClient(policyMap, clientRefGuid, matchingPayment) {
  const clientObj = policyMap?.get(clientRefGuid);
  if (clientObj) {
      clientObj.splitPercentage = null;
      this.setPaymentSplitPrct(matchingPayment, null);
  }
}

/**
 * Evenly distributes the split percentage across all clients marked as payees 
 * for a specific policy and updates the associated payment object.
 *
 * @param policyMap - Client mapping under a policy
 * @param matchingPayment - Associated ClaimPaymentDO object
 */
assignSplitToPayees(policyMap, matchingPayment) {
  const payeeClients = Array.from(policyMap.values()).filter((client: { isPayee?: boolean }) => client.isPayee);
  const totalSplit = payeeClients.length > 0 ? 100 / payeeClients.length : 0;
 
  this.setPaymentSplitPrct(matchingPayment, totalSplit);
 
  payeeClients.forEach((client: { splitPercentage?: number }) => {
      client.splitPercentage = totalSplit;
  });
}

/**
 * Updates the split percentage in the provided payment object.
 *
 * @param paymentDO - ClaimPaymentDO object
 * @param splitPrct - Split percentage to set
 */
setPaymentSplitPrct(paymentDO, splitPrct) {
  if (paymentDO) {
      paymentDO.paymentSplitPrct = splitPrct;
  }
}

/**
 * Removes a client entry from the role-to-policy client map.
 *
 * @param rolecd - Role code (e.g., NOMINEE)
 * @param policyNum - Policy number
 * @param clientRefGuid - Unique identifier for the client
 * @param rolePolicyClientMap - Map of role-to-policy-to-clients
 */
removeClientFromMap(rolecd,policyNum,clientRefGuid,rolePolicyClientMap){
  let roleMap = rolePolicyClientMap.get(rolecd);
  // if(roleMap && roleMap.has(policyNum)){
    let clientMap = roleMap.get(policyNum);
    if(roleMap && roleMap.has(clientRefGuid)){
      roleMap.delete(clientRefGuid);
    }
  // }
}

/**
 * Removes claim payment entries for clients marked for deletion.
 *
 * @param deletedClientRefGuids - Array of client reference GUIDs to delete
 * @param claimPayments - Map of clientRefGuid to ClaimPaymentDO
 * @returns A filtered map excluding the deleted clients
 */
removeClientFromClaimPayments(deletedClientRefGuids, claimPayments) {
  const deletedGuidsSet = new Set(deletedClientRefGuids);
  const filteredMap = new Map();
  for (const [guid, payment] of claimPayments) {
    if (!deletedGuidsSet.has(guid)) {
      filteredMap.set(guid, payment);
    }
  }
  return filteredMap;
}

/**
 * Adds a role entry to the payload if it does not already exist.
 * Updates nominee split and relationship code if already present.
 *
 * @param payload - The target payload object
 * @param DO - Data object key (e.g., claim)
 * @param role - Role code to add
 * @param clientRefGuid - Client identifier
 * @param newRole - Role object to insert
 * @returns The updated payload
 */
addRoleToPayloadIfNotExists(payload: any, DO: string, role: string, clientRefGuid: string, newRole: any): any {
  let clientList = DO == 'caseDO' ? (payload[DO].caseClients || []) : (payload[DO].proposalClients || []);
    const existingInPayload = clientList.find(existing =>
    existing.roleCd === role && existing.clientRefGuid === clientRefGuid
  );

  if (!existingInPayload) {
    let caseOrProposalClient = DO == 'caseDO' ? 'caseClient' : 'proposalClients';
    payload[DO][caseOrProposalClient].push(newRole);

  }else{
    existingInPayload.splitPercentage = newRole.splitPercentage;
    existingInPayload.relationCd = newRole.relationCd;
    existingInPayload.nomineeForInsured = newRole.nomineeForInsured;
  }

  return payload;
}
/**
 * Processes the client roles and maps them to the payload under the given data object.
 * Only selected INSURED roles are added.
 *
 * @param payload - Payload object to update
 * @param DO - Data object key
 * @param claimClientRoles - Nested map of roles → policy → clients
 * @returns The updated payload
 */
processClaimClientRoles(payload, DO,claimClientRoles): any {
  claimClientRoles.forEach((policyMap, role) => {
    // policyMap.forEach((clientMap, policyNum) => {
      policyMap.forEach((clientObj, clientRefGuid) => {
        // if (role !== Constants.ROLES.INSURED || clientObj.selected === true) {
          const newRole = {
            clientRefGuid: clientRefGuid,
            roleCd: role,
            splitPercentage: role === Constants.ROLES.PAYEE || role === Constants.ROLES.NOMINEE ? clientObj.splitPercentage : undefined,
            relationCd: clientObj.relationship,
            nomineeForInsured: clientObj.nomineeForInsured
          };
          payload = this.addRoleToPayloadIfNotExists(payload, DO, role, clientRefGuid, newRole);
        // }
      });
    // });
  });
  return payload;
}
/**
 * Processes the claimant hash map and maps them to the payload under the given data object.
 *
 * @param payload - The payload object
 * @param DO - Data object key
 * @param claimantHM - Map of clientRefGuid to claimant role data
 * @returns The updated payload
 */
processClaimantHM(payload, DO,claimantHM): any {
  if (claimantHM) {
    claimantHM.forEach((claimantData, clientRefGuid) => {
      const newRole = {
        clientRefGuid: clientRefGuid,
        roleCd: claimantData.role
      };
      payload = this.addRoleToPayloadIfNotExists(payload, DO, claimantData.role, clientRefGuid, newRole);
    });
  }
  return payload;
}
/**
 * Finds the key in the payment map for a given client and policy combination.
 *
 * @param clientRefGuid - Client identifier
 * @param policyNum - Policy number
 * @param paymentDO - Map of payments
 * @returns The matching key if found; otherwise, undefined
 */
findPaymentKey(clientRefGuid: string, policyNum: string,paymentDO){
  for (const [key, value] of paymentDO.entries()) {
    if (value.clientRefGuid === clientRefGuid && value.policyNum === policyNum) {
      return key;
    }
  }
  return undefined;
}
/**
 * Filters the `claimClientRoles` in the payload by retaining only valid entries 
 * present in the reference client-role mapping.
 *
 * @param payload - Payload object containing client roles
 * @param DO - Data object key
 * @param claimClientRoles_HM - Reference map of roles to policy to clients
 * @returns The filtered payload
 */
processPayloadAndClaimClientRoles(payload, DO, claimClientRoles_HM) {
  let clientList = DO == 'caseDO' ? (payload[DO].caseClients || []) : (payload[DO].proposalClients || []);
  let caseOrProposalClient = DO == 'caseDO' ? 'caseClient' : 'proposalClients';
  payload[DO][caseOrProposalClient] = clientList.filter((clientRole) => {
    const { clientRefGuid, policyNum, roleCd } = clientRole;
    return claimClientRoles_HM.get(roleCd)?.has(clientRefGuid);
  });
  return payload;
}
/**
 * Updates matching fields from the updated payment object into the existing one.
 *
 * @param existingPayDO - Original payment object
 * @param updatedPayDO - Updated fields to merge
 */
updatePaymentMatchingFields(existingPayDO, updatedPayDO){
  Object.keys(updatedPayDO).forEach(key => {
    if (existingPayDO.hasOwnProperty(key)) {
      existingPayDO[key] = updatedPayDO[key];
    }
  });
}

isClaimantLikeRole(role: string): boolean {
  return this.ROLES.includes(role);
}
}
